import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { UserProfile, AccountStatus } from '../types';
import AuditService from './AuditService';
import PlaceholderDataService from './PlaceholderDataService';

class UserManagementService {
  private readonly USERS_COLLECTION = 'users';

  /**
   * Check if using placeholder data
   */
  private usePlaceholderData(): boolean {
    return import.meta.env.VITE_USE_PLACEHOLDER_DATA === 'true';
  }

  /**
   * Get all users with pagination
   */
  async getAllUsers(
    pageLimit: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<{ users: UserProfile[]; lastDoc: DocumentSnapshot | null }> {
    try {
      // Use placeholder data if enabled
      if (this.usePlaceholderData()) {
        const users = PlaceholderDataService.getUsers();
        return { users, lastDoc: null };
      }

      const usersRef = collection(db, this.USERS_COLLECTION);
      let q = query(usersRef, orderBy('createdAt', 'desc'), limit(pageLimit));

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const users: UserProfile[] = [];
      let lastDocument: DocumentSnapshot | null = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          uid: doc.id,
          email: data.email,
          displayName: data.displayName,
          phoneNumber: data.phoneNumber,
          role: data.role,
          accountStatus: (data.accountStatus as AccountStatus) || 'active', // Fallback for backwards compatibility
          isGuestMode: data.isGuestMode || false,
          termsAccepted: data.termsAccepted || false,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
        });
      });

      if (querySnapshot.docs.length > 0) {
        lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
      }

      return { users, lastDoc: lastDocument };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Failed to fetch users');
    }
  }

  /**
   * Get single user by ID
   */
  async getUserById(userId: string): Promise<UserProfile | null> {
    try {
      // Use placeholder data if enabled
      if (this.usePlaceholderData()) {
        const users = PlaceholderDataService.getUsers();
        const user = users.find((u) => u.uid === userId);
        if (user) {
          await AuditService.logAdminAction('view_user', {
            targetUserId: userId,
            targetUserEmail: user.email,
          });
        }
        return user || null;
      }

      const userRef = doc(db, this.USERS_COLLECTION, userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        await AuditService.logAdminAction('view_user', {
          targetUserId: userId,
          targetUserEmail: data.email,
        });

        return {
          uid: userSnap.id,
          email: data.email,
          displayName: data.displayName,
          phoneNumber: data.phoneNumber,
          role: data.role,
          accountStatus: (data.accountStatus as AccountStatus) || 'active',
          isGuestMode: data.isGuestMode || false,
          termsAccepted: data.termsAccepted || false,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user');
    }
  }

  /**
   * Delete user account
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      // Get user info for audit log
      const userToDelete = await this.getUserById(userId);

      if (!userToDelete) {
        throw new Error('User not found');
      }

      // Placeholder data doesn't support deletes (read-only)
      if (this.usePlaceholderData()) {
        console.warn('Cannot delete users in placeholder data mode');
        await AuditService.logAdminAction('delete_user', {
          targetUserId: userId,
          targetUserEmail: userToDelete.email,
          reason: 'Admin action (placeholder mode)',
        });
        return;
      }

      // Delete user document from Firestore
      const userRef = doc(db, this.USERS_COLLECTION, userId);
      await deleteDoc(userRef);

      // Log the action
      await AuditService.logAdminAction('delete_user', {
        targetUserId: userId,
        targetUserEmail: userToDelete.email,
        reason: 'Admin action',
      });

      console.log('User deleted successfully:', userId);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  /**
   * Send password reset email
   */
  async resetUserPassword(email: string): Promise<void> {
    try {
      // In placeholder mode, just log the action
      if (this.usePlaceholderData()) {
        console.warn('Cannot send password reset in placeholder data mode');
        await AuditService.logAdminAction('password_reset', {
          targetUserEmail: email,
        });
        console.log('Password reset simulated for:', email);
        return;
      }

      await sendPasswordResetEmail(auth, email);

      // Log the action
      await AuditService.logAdminAction('password_reset', {
        targetUserEmail: email,
      });

      console.log('Password reset email sent to:', email);
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw new Error('Failed to send password reset email');
    }
  }

  /**
   * Search users by email or name
   */
  async searchUsers(searchQuery: string): Promise<UserProfile[]> {
    try {
      // Use placeholder data if enabled
      if (this.usePlaceholderData()) {
        const allUsers = PlaceholderDataService.getUsers();
        const lowerQuery = searchQuery.toLowerCase();
        return allUsers.filter(
          (user) =>
            user.email.toLowerCase().includes(lowerQuery) ||
            user.displayName?.toLowerCase().includes(lowerQuery) ||
            user.uid.toLowerCase().includes(lowerQuery) ||
            user.phoneNumber?.includes(searchQuery)
        );
      }

      const usersRef = collection(db, this.USERS_COLLECTION);

      // Search by email (Firestore doesn't support full-text search, so we'll use prefix match)
      const q = query(
        usersRef,
        where('email', '>=', searchQuery),
        where('email', '<=', searchQuery + '\uf8ff'),
        limit(20)
      );

      const querySnapshot = await getDocs(q);
      const users: UserProfile[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          uid: doc.id,
          email: data.email,
          displayName: data.displayName,
          phoneNumber: data.phoneNumber,
          role: data.role,
          accountStatus: (data.accountStatus as AccountStatus) || 'active',
          isGuestMode: data.isGuestMode || false,
          termsAccepted: data.termsAccepted || false,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
        });
      });

      return users;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  /**
   * Update user profile (NEW METHOD)
   */
  async updateUser(
    userId: string,
    updates: Partial<UserProfile>
  ): Promise<void> {
    try {
      // Placeholder data doesn't support updates (read-only)
      if (this.usePlaceholderData()) {
        console.warn('Cannot update users in placeholder data mode');
        // Still log the audit action for demonstration
        await AuditService.logAdminAction('update_user', {
          targetUserId: userId,
          targetUserEmail: updates.email,
          changes: updates,
        });
        return;
      }

      const userRef = doc(db, this.USERS_COLLECTION, userId);

      // Remove read-only fields
      const { uid, createdAt, lastLoginAt, ...updateData } = updates;

      await updateDoc(userRef, updateData);

      // Log the action
      await AuditService.logAdminAction('update_user', {
        targetUserId: userId,
        targetUserEmail: updates.email,
        changes: updateData,
      });

      console.log('User updated successfully:', userId);
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user');
    }
  }

  /**
   * Update user account status (NEW METHOD)
   */
  async updateAccountStatus(
    userId: string,
    status: AccountStatus
  ): Promise<void> {
    try {
      // Placeholder data doesn't support updates (read-only)
      if (this.usePlaceholderData()) {
        console.warn('Cannot update account status in placeholder data mode');
        await AuditService.logAdminAction('update_account_status', {
          targetUserId: userId,
          newStatus: status,
        });
        return;
      }

      const userRef = doc(db, this.USERS_COLLECTION, userId);

      await updateDoc(userRef, {
        accountStatus: status,
      });

      // Log the action
      await AuditService.logAdminAction('update_account_status', {
        targetUserId: userId,
        newStatus: status,
      });

      console.log('Account status updated successfully:', userId, status);
    } catch (error) {
      console.error('Error updating account status:', error);
      throw new Error('Failed to update account status');
    }
  }
}

export default new UserManagementService();
